import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["item"];
  static values = { images: Array };

  connect() {
    // Build the images array: prefer imagesValue if provided; else derive from visible thumbnails.
    if (this.hasImagesValue && Array.isArray(this.imagesValue) && this.imagesValue.length) {
      this.images = this.imagesValue.slice();
    } else {
      this.images = this.itemTargets
        .map(el => {
          const img = el.querySelector("img");
          return img && (img.dataset.original || img.dataset.src || img.src);
        })
        .filter(Boolean);
    }

    this.setupItemClicks();
    this.modal = null;
    this.intersectionObserver = null; // will be used for modal-local lazy-loading
  }
    setupItemClicks() {
    this.itemTargets.forEach((el) => {
        el.addEventListener("click", () => {
        // use data-gallery-popup-index-param if present
        const idxAttr = el.dataset.galleryPopupIndexParam;
        let index;
        if (idxAttr !== undefined) {
            index = parseInt(idxAttr, 10);
        } else {
            // the cover image (no param) should map to -1 or handle separately
            index = -1; 
        }

        // map index to this.images array
        // cover image is not in images array, so first extra image is index 0
        this.open(index);
        });
    });
    }

  // -------------------------
  // Open / Close / Navigation
  // -------------------------
  open(index = 0) {
    this.currentIndex = index === -1 ? 0 : index;
    this.createModal();
    this.updateMainImage(true);
    this.showModal();
    document.addEventListener("keydown", this.keyHandler);
    this.initSwipe();
  }

  close = () => {
    if (this.modal && this.modal.parentNode) this.modal.remove();
    this.modal = null;
    document.removeEventListener("keydown", this.keyHandler);
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  };

  next = () => {
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.updateMainImage(true);
  };

  prev = () => {
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    this.updateMainImage(true);
  };

  keyHandler = (e) => {
    if (e.key === "ArrowRight") this.next();
    if (e.key === "ArrowLeft") this.prev();
    if (e.key === "Escape") this.close();
  };

  showModal() {
    requestAnimationFrame(() => {
      this.modal.classList.add("visible");
    });
  }

  // -------------------------
  // DOM: build modal and thumbs
  // -------------------------
  createModal() {
    // if already open, remove and recreate (simple)
    if (this.modal && this.modal.parentNode) this.modal.remove();

    this.modal = document.createElement("div");
    this.modal.className = "image-popup-modal";

    this.modal.innerHTML = `
      <div class="image-popup-backdrop"></div>

      <button class="image-popup-close" aria-label="Close">×</button>
      <button class="image-popup-prev" aria-label="Previous">‹</button>
      <button class="image-popup-next" aria-label="Next">›</button>

      <div class="image-popup-topbar">
        <div class="image-popup-counter"></div>
      </div>

      <div class="image-popup-body">
        <div class="image-popup-container">
          <img class="image-popup-photo" alt="Photo" />
        </div>

        <div class="image-popup-thumbs-wrapper">
          <div class="image-popup-thumbnails"></div>
        </div>
      </div>
    `;

    // listeners
    this.modal.querySelector(".image-popup-backdrop").addEventListener("click", this.close);
    this.modal.querySelector(".image-popup-close").addEventListener("click", this.close);
    this.modal.querySelector(".image-popup-prev").addEventListener("click", this.prev);
    this.modal.querySelector(".image-popup-next").addEventListener("click", this.next);

    document.body.appendChild(this.modal);

    // cache nodes
    this.mainImg = this.modal.querySelector(".image-popup-photo");
    this.counterEl = this.modal.querySelector(".image-popup-counter");
    this.thumbsContainer = this.modal.querySelector(".image-popup-thumbnails");

    // build thumbs and setup modal-local lazy loader
    this.buildThumbnails();
    this.setupModalLazyLoader();
    this.highlightThumb();
  }

  buildThumbnails() {
    this.thumbsContainer.innerHTML = "";
    this.images.forEach((url, idx) => {
      const wrapper = document.createElement("div");
      wrapper.className = "image-popup-thumb-item";

      const thumb = document.createElement("img");
      // use placeholder (tiny transparent 1x1 or blank) so that lazy loader sets real image from data-src
      // we rely on lazy behavior for thumbs: data-src + class lazy
      thumb.dataset.src = url;
      thumb.alt = `Image ${idx + 1}`;
      thumb.loading = "lazy";
      thumb.className = "image-popup-thumb lazy";

      wrapper.addEventListener("click", () => {
        this.currentIndex = idx;
        this.updateMainImage(true);
        this.scrollThumbIntoView(idx);
      });

      wrapper.appendChild(thumb);
      this.thumbsContainer.appendChild(wrapper);
    });
  }

  // -------------------------
  // Lazy loading inside modal (same approach as your lazy-load controller)
  // -------------------------
  setupModalLazyLoader() {
    // If an old observer exists, disconnect
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    const imgNodes = this.modal.querySelectorAll("img.lazy");

    this.intersectionObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          const src = img.dataset.src;
          if (src) {
            img.src = src;
            img.classList.remove("lazy");
            observer.unobserve(img);
          }
        }
      });
    }, { root: null, rootMargin: "200px", threshold: 0.01 });

    imgNodes.forEach(img => {
      this.intersectionObserver.observe(img);
    });
  }

  // -------------------------
  // Update main image, counter, preload neighbors, highlight thumbs
  // -------------------------
  updateMainImage(forceImmediate = false) {
    if (!this.modal) return;

    const total = this.images.length;
    this.counterEl.textContent = `${this.currentIndex + 1} / ${total}`;

    const url = this.images[this.currentIndex];

    if (forceImmediate) {
      // show main immediately (set src directly)
      this.mainImg.src = url;
      this.mainImg.removeAttribute("data-src");
      this.mainImg.loading = "eager";
    } else {
      // lazy load variant
      this.mainImg.dataset.src = url;
      this.mainImg.loading = "lazy";
      this.mainImg.classList.add("lazy");
      // ensure modal-local lazy loader will pick it up
      if (this.intersectionObserver) {
        this.intersectionObserver.observe(this.mainImg);
      }
    }

    // preload neighbors (non-blocking)
    this.preload((this.currentIndex + 1) % total);
    this.preload((this.currentIndex - 1 + total) % total);

    this.highlightThumb();
    this.scrollThumbIntoView(this.currentIndex);
  }

  preload(i) {
    const idx = (i + this.images.length) % this.images.length;
    const url = this.images[idx];
    const img = new Image();
    img.src = url;
  }

  // -------------------------
  // Thumbnails: highlight active in red and ensure center view
  // -------------------------
  highlightThumb() {
    const wrappers = Array.from(this.thumbsContainer.querySelectorAll(".image-popup-thumb-item"));
    wrappers.forEach((wrap, i) => {
      if (i === this.currentIndex) {
        wrap.classList.add("active");
        const img = wrap.querySelector("img");
        if (img && !img.src) {
          img.src = img.dataset.src || "";
        }
      } else {
        wrap.classList.remove("active");
      }
    });
  }

  scrollThumbIntoView(idx) {
    const wrappers = Array.from(this.thumbsContainer.querySelectorAll(".image-popup-thumb-item"));
    const el = wrappers[idx];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }

  // -------------------------
  // Swipe support (touch + desktop drag)
  // -------------------------
  initSwipe() {
    const container = this.modal.querySelector(".image-popup-container");
    if (!container) return;

    let startX = 0;
    let startY = 0;
    let dragging = false;

    const onStart = (e) => {
      dragging = true;
      const point = e.touches ? e.touches[0] : e;
      startX = point.clientX;
      startY = point.clientY;
      this.mainImg.style.transition = "none";
    };

    const onMove = (e) => {
      if (!dragging) return;
      const point = e.touches ? e.touches[0] : e;
      const dx = point.clientX - startX;
      const dy = point.clientY - startY;
      // ignore vertical heavy movement
      if (Math.abs(dy) > Math.abs(dx)) return;
      this.mainImg.style.transform = `translateX(${dx * 0.3}px)`;
    };

    const onEnd = (e) => {
      if (!dragging) return;
      dragging = false;
      const point = (e.changedTouches && e.changedTouches[0]) || e;
      const dx = point.clientX - startX;
      this.mainImg.style.transition = "transform .2s ease";
      this.mainImg.style.transform = "";

      if (Math.abs(dx) > 60) {
        if (dx < 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    };

    container.addEventListener("touchstart", onStart, { passive: true });
    container.addEventListener("touchmove", onMove, { passive: true });
    container.addEventListener("touchend", onEnd);

    container.addEventListener("mousedown", onStart);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
  }
}